"use client"

import { useState, useRef, useEffect, useCallback, useTransition } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import { ChevronDown } from "lucide-react"
import ReactCountryFlag from "react-country-flag"
import {
  DISPLAY_PARAM,
  displayCurrencyOptions,
} from "@lib/util/display-currency"

export type RegionOption = {
  id: string
  name: string
  currency_code: string
  /** ISO-2 codes, for the flag icon only. */
  countries: string[]
}

type RegionSelectorProps = {
  regions: RegionOption[]
  selectedRegionId?: string
  /** Chosen display currency, or null for "same as the region's". */
  displayCurrency?: string | null
}

/** Codes only — the panel is narrow and the code is what customers recognise. */
const CURRENCY_OPTIONS = displayCurrencyOptions()

// Unicode flag emoji render as plain letters (or blank boxes) on systems without
// a colour-emoji font — common on Windows and many Linux desktops. Real flag
// icons (SVG, via the CDN react-country-flag already uses elsewhere in this
// codebase) render identically everywhere.
const Flag = ({ countryCode }: { countryCode: string }) => (
  // @ts-ignore — svg mode's props aren't typed on the emoji-mode overload
  <ReactCountryFlag
    svg
    countryCode={countryCode}
    style={{ width: "16px", height: "16px" }}
  />
)

/**
 * A region only gets a flag when it maps to exactly one country. "Europe" spans
 * six, so any single flag would misrepresent it.
 */
const flagFor = (region: RegionOption) =>
  region.countries.length === 1 ? region.countries[0].toUpperCase() : null

/**
 * The single region selector. Redmine #166532.
 *
 * One dropdown, labelled "<Region Name> (<CURRENCY>)". There is deliberately no
 * separate currency picker: currency is a property of the region, and letting
 * them be chosen independently is what allowed the selector, the displayed
 * prices and the cart to disagree.
 *
 * Selecting a region navigates to the same path with ?currency=<CURRENCY>.
 * The middleware turns that into the region and sets the _medusa_region_id
 * cookie, so the URL stays the single public contract — which is also what
 * Google Feed landing pages rely on. Region IDs are never shown to customers.
 */
const RegionSelector = ({
  regions,
  selectedRegionId,
  displayCurrency,
}: RegionSelectorProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)
  const [isPending, startTransition] = useTransition()
  const busy = switching || isPending
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 })

  const selected =
    regions.find((r) => r.id === selectedRegionId) ?? regions[0]

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    setPanelPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    updatePosition()
    window.addEventListener("resize", updatePosition)
    window.addEventListener("scroll", updatePosition, true)
    return () => {
      window.removeEventListener("resize", updatePosition)
      window.removeEventListener("scroll", updatePosition, true)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isOpen])

  /**
   * Display currency is presentation only, so unlike a region change there is no
   * cart to migrate — just re-render with the new preference.
   *
   * A full navigation rather than router.push(): the provider that carries this
   * value lives in the ROOT layout, and Next does not re-render a root layout on
   * client-side navigation, so router.push left every price unchanged. The
   * middleware validates and persists the value on the way through.
   */
  const selectDisplayCurrency = (code: string) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set(DISPLAY_PARAM, code)
    setIsOpen(false)
    window.location.assign(`${pathname}?${params.toString()}`)
  }

  const selectRegion = async (regionId: string) => {
    const region = regions.find((r) => r.id === regionId)
    if (!region || region.id === selected?.id) {
      setIsOpen(false)
      return
    }

    setError(null)
    setSwitching(true)

    // Move the cart first. If it can't be migrated the region must not change
    // either — a cart priced in one currency while the selector says another is
    // exactly the mismatch this ticket exists to prevent.
    try {
      const res = await fetch("/api/cart/region", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ region_id: region.id }),
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok || !data?.success) {
        setError(
          data?.error ||
            "Your basket could not be moved to that region. Please try again."
        )
        setSwitching(false)
        return
      }
    } catch {
      setError("Could not change region. Please check your connection.")
      setSwitching(false)
      return
    }

    // Preserve every unrelated query parameter — listing pages carry filters,
    // sort order and pagination that must survive a currency change.
    const params = new URLSearchParams(searchParams.toString())
    params.set("currency", region.currency_code.toUpperCase())

    setSwitching(false)
    setIsOpen(false)
    startTransition(() => {
      router.push(`${pathname}?${params.toString()}`)
      // Prices come from server components, so the route has to re-render for
      // the new region — a URL change alone would serve the cached payload.
      router.refresh()
    })
  }

  if (!regions.length || !selected) {
    return null
  }

  const selectedFlag = flagFor(selected)

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={busy}
        className="flex items-center gap-1.5 text-white hover:opacity-80 transition-opacity duration-200 cursor-pointer disabled:opacity-60"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label="Change region and currency"
        data-testid="region-selector-trigger"
      >
        <span className="flex items-center gap-1.5">
          Ship to
          {selectedFlag && <Flag countryCode={selectedFlag} />}
          {selected.name} ({selected.currency_code.toUpperCase()})
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          className="fixed w-[360px] bg-white rounded-lg shadow-xl p-6 z-[100000]"
          style={{ top: panelPos.top, right: panelPos.right }}
          data-testid="region-selector-panel"
        >
          <p className="text-sm text-gray-900 font-medium mb-1">
            Select your region
          </p>
          <p className="text-xs text-gray-500 mb-4">
            Prices and checkout use the region&apos;s currency.
          </p>

          <label
            htmlFor="region-select"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Region
          </label>
          <div className="flex items-center gap-2">
            {selectedFlag && <Flag countryCode={selectedFlag} />}
            <select
              id="region-select"
              value={selected.id}
              onChange={(e) => selectRegion(e.target.value)}
              disabled={busy}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none disabled:opacity-60"
              data-testid="region-select"
            >
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name} ({region.currency_code.toUpperCase()})
                </option>
              ))}
            </select>
          </div>

          <label
            htmlFor="display-currency-select"
            className="block text-sm font-medium text-gray-700 mt-4 mb-1"
          >
            Show prices in
          </label>
          <select
            id="display-currency-select"
            value={displayCurrency ?? selected.currency_code}
            onChange={(e) => selectDisplayCurrency(e.target.value)}
            disabled={busy}
            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none disabled:opacity-60"
            data-testid="display-currency-select"
          >
            {CURRENCY_OPTIONS.map((o) => (
              <option key={o.code} value={o.code}>
                {o.code.toUpperCase()}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-gray-500">
            Display only. You are charged in{" "}
            {selected.currency_code.toUpperCase()}.
          </p>

          {busy && (
            <p className="mt-3 text-xs text-gray-500">Updating your basket…</p>
          )}

          {error && (
            <p
              className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800"
              data-testid="region-switch-error"
            >
              {error}
            </p>
          )}
        </div>
      )}
    </>
  )
}

export default RegionSelector
